import { createContext, type FC, type ReactNode, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useThreadsQuery } from "../components/StaffCommunication/util/hooks";
import { trackEvent } from "../utils/analytics";
import type { RecommendationOpenSource } from "../utils/models";
import { useChosenDepartment } from "./ChosenDepartment";
import { SidePanelHostSetterContext, SidePanelHostValueContext } from "./sidePanel/SidePanelHostContext";
import type { SidePanelHost, SidePanelOpener, SidePanelTab } from "./sidePanel/sidePanelTypes";

type OpenOptions = {
	/** The tab to open on. Absent = the tab an already-open panel shows, or the drawer trigger's own (see `activeTab`). */
	tab?: SidePanelTab;
	/** A request to scroll to on the Requests tab — the `?requestId=` deep link. */
	requestId?: string;
};

type RecommendationsDrawerProps = {
	/** The admin side panel is showing: docked beside a staffing page, or over the page it was opened on. */
	isOpen: boolean;
	/** `null` = the drawer trigger's own tab (Requests; Chat in a synergy department), resolved by the panel. */
	activeTab: SidePanelTab | null;
	requestId: string | null;
	openedFrom: SidePanelOpener;
	/** Opens (on `options.tab`, else the open panel's tab or the trigger's own) or closes the panel, as a staffing page's drawer trigger. */
	toggleDrawerOpen: (open: boolean, source: RecommendationOpenSource, options?: OpenOptions) => void;
	/** Opens the panel on Changes, as the header bell. The bell tracks its own open. */
	openFromBell: () => void;
	/** Closes the panel without the drawer's close event — a bell-opened panel, or a link inside it leaving the page. */
	closePanel: () => void;
	setActiveTab: (tab: SidePanelTab) => void;
};

type PanelState = {
	open: boolean;
	tab: SidePanelTab | null;
	requestId: string | null;
	openedFrom: SidePanelOpener;
	/** The navigation (`location.key`) the panel was opened on: an overlay belongs to that page only. */
	openedAtKey: string;
	/**
	 * Opened on a staffing page (docked): it stays open across staffing pages, hides on any other and comes back on the
	 * next one. Otherwise it was an overlay over the page it was opened on, and the first navigation closes it for good.
	 */
	opensOnStaffingPages: boolean;
};

const RecommendationsDrawerContext = createContext<RecommendationsDrawerProps | null>(null);

/**
 * The admin side panel's state — one panel for the bell and the staffing drawer trigger alike (`UpdatesPanel`). Also
 * holds the staffing page registered under it (`useSidePanelHost`): a panel opened on a staffing page docks beside it
 * and stays open across navigation between staffing pages, as the drawer always did; one opened anywhere else overlays
 * that page and any navigation closes it, so a link inside it lands on its page and it never reappears docked.
 */
export const RecommendationsDrawerProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const location = useLocation();
	const [host, setHost] = useState<SidePanelHost | null>(null);
	const [state, setState] = useState<PanelState>(() => ({
		open: false,
		// DEFAULT: nothing chosen yet — every open names its tab (or the trigger's own), so this is never shown.
		tab: null,
		requestId: null,
		// DEFAULT: never read while closed; every open sets it.
		openedFrom: "drawer",
		openedAtKey: location.key,
		// DEFAULT: never read while closed; every open sets it.
		opensOnStaffingPages: false,
	}));

	// Read by the open callbacks (stable identities); a layout effect, so it is current before any page's open effect.
	const locationKeyRef = useRef(location.key);
	useLayoutEffect(() => {
		locationKeyRef.current = location.key;
	}, [location.key]);
	// The bell asks it at click time, long after the page under it registered (or didn't).
	const hostRef = useRef<SidePanelHost | null>(null);
	const registerHost = useCallback((next: SidePanelHost | null) => {
		hostRef.current = next;
		setHost(next);
	}, []);

	// A navigation closes an overlay for good, so a link inside it lands on its page and it never comes back docked on
	// a later staffing page. Adjusted during render ("reset on a changed value").
	const [seenLocationKey, setSeenLocationKey] = useState(location.key);
	if (seenLocationKey !== location.key) {
		setSeenLocationKey(location.key);
		if (state.open && !state.opensOnStaffingPages) setState((previous) => ({ ...previous, open: false, requestId: null }));
	}

	const toggleDrawerOpen = useCallback((open: boolean, source: RecommendationOpenSource, options?: OpenOptions) => {
		setState((previous) =>
			open
				? {
						open: true,
						// DEFAULT: an open that names no tab keeps the tab an open panel shows; a closed panel opens on the
						// trigger's own tab (null), which depends on the department (see `activeTab`).
						tab: options?.tab ?? (previous.open ? previous.tab : null),
						// DEFAULT: no deep link — nothing to scroll to.
						requestId: options?.requestId ?? null,
						openedFrom: "drawer",
						openedAtKey: locationKeyRef.current,
						// Every drawer trigger (the toolbar button, a day card, the `?requestId=` link) sits on a staffing page.
						// Its open can run before that page's own registration effect, so it is not read from `hostRef`.
						opensOnStaffingPages: true,
					}
				: { ...previous, open: false, requestId: null },
		);
		open ? trackEvent("recommendations_drawer_opened", { source }) : trackEvent("recommendations_drawer_closed", { source });
	}, []);

	const openFromBell = useCallback(() => {
		setState({
			open: true,
			tab: "changes",
			requestId: null,
			openedFrom: "bell",
			openedAtKey: locationKeyRef.current,
			opensOnStaffingPages: hostRef.current !== null,
		});
	}, []);

	const closePanel = useCallback(() => setState((previous) => ({ ...previous, open: false, requestId: null })), []);
	const setActiveTab = useCallback((tab: SidePanelTab) => setState((previous) => ({ ...previous, tab })), []);

	const isOpen = state.open && (state.opensOnStaffingPages ? host !== null : state.openedAtKey === location.key);
	const value = useMemo<RecommendationsDrawerProps>(
		() => ({
			isOpen,
			activeTab: state.tab,
			requestId: state.requestId,
			openedFrom: state.openedFrom,
			toggleDrawerOpen,
			openFromBell,
			closePanel,
			setActiveTab,
		}),
		[isOpen, state.tab, state.requestId, state.openedFrom, toggleDrawerOpen, openFromBell, closePanel, setActiveTab],
	);

	return (
		<RecommendationsDrawerContext.Provider value={value}>
			<SidePanelHostSetterContext.Provider value={registerHost}>
				<SidePanelHostValueContext.Provider value={host}>{children}</SidePanelHostValueContext.Provider>
			</SidePanelHostSetterContext.Provider>
		</RecommendationsDrawerContext.Provider>
	);
};

export const useRecommendationsDrawerContext = (): RecommendationsDrawerProps => {
	const context = useContext(RecommendationsDrawerContext);

	if (!context) {
		throw Error("`useRecommendationsDrawerContext` must be used within a `RecommendationsDrawerContext`");
	}

	return context;
};

export type DrawerTriggerNotification = {
	tooltipText: string;
	color: "error" | "success" /*THIS is NOT to indicate an error state, but rather color it as green or red. */;
} | null;

/**
 * Sibling to `useRecommendationsDrawerContext` — deliberately NOT folded into it. Only the 4 call
 * sites that render the CopilotButton-opening trigger need the notification computation (which polls
 * `useThreadsQuery` every 2 minutes); the other `useRecommendationsDrawerContext` call sites only want
 * `isOpen`/`toggleDrawerOpen` and would otherwise pick up redundant polling subscribers for free.
 */
export const useDrawerTriggerNotification = () => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const { data } = useThreadsQuery(chosenDepartment.department_id);
	const unreadMessagesCount = useMemo(() => Object.values(data?.unreadCounts ?? {}).reduce((sum, count) => sum + count, 0), [data]);

	const getDrawerTriggerNotification = useCallback(
		(requestsWithResponsesCount: number): DrawerTriggerNotification => {
			if (unreadMessagesCount === 0 && requestsWithResponsesCount === 0) return null;
			const parts: string[] = [];
			if (unreadMessagesCount > 0) parts.push(t("drawerTrigger.newMessages", { count: unreadMessagesCount }));
			if (requestsWithResponsesCount > 0) parts.push(t("drawerTrigger.requestsWithResponses", { count: requestsWithResponsesCount }));
			// Requests take priority when both are present — a pending pickup response needs action,
			// an unread message is just informational.
			return { tooltipText: parts.join(" · "), color: requestsWithResponsesCount > 0 ? "error" : "success" };
		},
		[unreadMessagesCount, t],
	);

	return { getDrawerTriggerNotification };
};
